import React, { useRef, useState } from 'react';
import { hrefForSameSiteAsset } from '../productMediaUtils';
import {
  CUSTOMER_PHOTO_ACCEPT,
  CUSTOMER_PHOTO_MAX_COUNT,
  uploadCustomerPhotoFile,
} from './customerPhotoUpload';

/**
 * Multiple customer photos for bespoke / custom jewelry requests.
 */
export default function ProductPhotoUploadMulti({
  id = 'jewelry-photos',
  label = 'Upload photos',
  hint = '',
  values = [],
  onChange,
  disabled = false,
  required = false,
  maxCount = CUSTOMER_PHOTO_MAX_COUNT,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const photos = Array.isArray(values) ? values : [];
  const atMax = photos.length >= maxCount;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || atMax) return;
    setError('');
    setBusy(true);
    try {
      const result = await uploadCustomerPhotoFile(file);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      onChange?.([...photos, result.url]);
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (index) => {
    if (disabled || busy) return;
    onChange?.(photos.filter((_, i) => i !== index));
  };

  return (
    <div className="jewelry-photo-upload jewelry-photo-upload--multi">
      <label className="jewelry-customizer__label" htmlFor={id}>
        {label}
        {required ? <span className="jewelry-customizer__req"> *</span> : null}
      </label>
      {hint ? <p className="jewelry-customizer__hint small text-muted mb-2">{hint}</p> : null}

      {photos.length > 0 ? (
        <ul className="jewelry-photo-upload__grid list-unstyled mb-2">
          {photos.map((url, index) => {
            const preview = hrefForSameSiteAsset(url);
            return (
              <li key={`${url}-${index}`} className="jewelry-photo-upload__tile">
                <img src={preview} alt={`Upload ${index + 1}`} />
                <button
                  type="button"
                  className="jewelry-photo-upload__remove"
                  disabled={disabled || busy}
                  aria-label={`Remove photo ${index + 1}`}
                  onClick={() => removeAt(index)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="jewelry-photo-upload__row">
        {!atMax ? (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm jewelry-photo-upload__pick"
            disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Uploading…' : photos.length === 0 ? 'Choose photo' : 'Add another photo'}
          </button>
        ) : (
          <p className="small text-muted mb-0">Maximum {maxCount} photos.</p>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={CUSTOMER_PHOTO_ACCEPT}
          className="visually-hidden"
          disabled={disabled || busy || atMax}
          onChange={onPick}
        />
      </div>

      {photos.length > 0 && !atMax ? (
        <p className="jewelry-customizer__hint small text-muted mb-0 mt-1">
          {photos.length} of {maxCount} — add more angles or close-ups if helpful.
        </p>
      ) : null}

      {error ? (
        <p className="jewelry-customizer__error small mb-0 mt-2" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
